import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import Header from '../components/Header'
import Footer from '../components/Footer'
import { useAuth } from '../auth/AuthProvider'
import { useLanguage } from '../i18n/LanguageProvider'
import { supabase } from '../lib/supabase'
import { langFromPath, withLang } from '../i18n/urlLang'
import type { EmailOtpType } from '@supabase/supabase-js'

type Status = 'loading' | 'expired' | 'invalid' | 'redirecting'

// Open-redirect defense (UI-SPEC §9 locked contract) : accepte uniquement un path
// interne commençant par un seul `/`. Rejette protocol-relative (`//evil.com`)
// et toute URL absolue (`https://evil.com`, `javascript:…`). Fallback silent sur
// `/dashboard` — le user vient de cliquer son magic link, pas de friction à ajouter.
function resolveNext(rawNext: string | null): string {
  if (!rawNext) return '/dashboard'
  if (!rawNext.startsWith('/')) return '/dashboard'
  if (rawNext.startsWith('//')) return '/dashboard'
  if (rawNext.includes('://')) return '/dashboard'
  return rawNext
}

// Auth email links (magic_link / confirmation templates, 2026-11-03) carry
// `?token_hash=&type=&redirect_to=<emailRedirectTo>`. redirect_to is the LAST
// param and its encoding is not guaranteed (GoTrue's html/template query-escapes
// it, but its value itself contains `?next=`), so take the raw tail after
// `redirect_to=` instead of URLSearchParams, decoding once if it is escaped.
// Only a same-origin URL is accepted; its `next` still goes through resolveNext().
function readRedirectTo(search: string): URL | null {
  const m = /[?&]redirect_to=/.exec(search)
  if (!m) return null
  let raw = search.slice(m.index + m[0].length)
  if (!/^https?:\/\//i.test(raw)) {
    try {
      raw = decodeURIComponent(raw)
    } catch {
      return null
    }
  }
  try {
    const url = new URL(raw)
    return url.origin === window.location.origin ? url : null
  } catch {
    return null
  }
}

export default function AuthCallback() {
  const navigate = useNavigate()
  const { session, loading } = useAuth()
  const { t } = useLanguage()

  // Bug B (Phase 28.2): true while the verifyOtp({ token_hash }) call for a
  // server-generated magic link is in flight. Folded into the `status` derivation
  // below so an in-flight verifyOtp shows "Connexion en cours…", NOT the
  // "Ce lien a expiré" UI — useAuth().loading flips to false before verifyOtp
  // resolves, so without this flag `status` would flicker through 'expired'.
  // Initialised lazily from the URL (runs once at mount, before first paint) so
  // a Bug B link starts in the 'loading' state with no setState-in-effect — the
  // verifyOtp effect below only has to *clear* the flag once the call settles.
  const [verifyingOtp, setVerifyingOtp] = useState(() => {
    const params = new URLSearchParams(window.location.search)
    return Boolean(params.get('token_hash') && params.get('type'))
  })

  // Invite links (inviteUserByEmail) use implicit flow and land with #access_token= in the
  // hash even when the project uses PKCE. supabase-js detectSessionInUrl does not process
  // hash tokens in PKCE mode — set the session manually when detected.
  useEffect(() => {
    const hash = window.location.hash
    if (!hash.includes('access_token=')) return
    const params = new URLSearchParams(hash.slice(1))
    const accessToken = params.get('access_token')
    const refreshToken = params.get('refresh_token')
    if (accessToken && refreshToken) {
      supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken })
    }
  }, [])

  // Bug B (Phase 28.2): server-generated magic links (stripe-webhook admin.generateLink)
  // carry ?token_hash= + ?type=. verifyOtp needs no code_verifier — works for an
  // admin-generated link, unlike the PKCE ?code= exchange. Its own useEffect, sibling
  // to the implicit-hash branch above (G-02 — no race: this URL has no ?code= so
  // detectSessionInUrl ignores it, and no #access_token= so the hash branch ignores it).
  // verifyingOtp gates the `status` useMemo so the in-flight window shows 'loading',
  // not the 'expired' fall-through (the flicker fix). On success: onAuthStateChange
  // flips `session` → existing status useMemo → existing redirect useEffect. On genuine
  // failure (reused/expired link): session stays null AFTER verifyingOtp clears →
  // existing 'expired' UI.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const tokenHash = params.get('token_hash')
    const otpType = params.get('type')
    if (!tokenHash || !otpType) return
    // verifyingOtp is already `true` (lazy init above) — only clear it once the
    // call settles. Clearing in an async .then()/.catch() callback is not a
    // setState-in-effect (the effect body itself sets no state).
    // WR-01: verifyOtp normally *resolves* with { data, error } but can also
    // *reject* (network failure, reused/expired link). Both shapes must be
    // handled: an unhandled rejection here surfaces as a noisy
    // `Uncaught (in promise)` and can trip Sentry/error boundaries.
    // WR-02: clear the flag only once the outcome is confirmed. On success the
    // session arrives via onAuthStateChange — keep verifyingOtp set until then
    // so `status` never falls through to the 'expired' flicker. On failure
    // (error present or rejection) clear immediately → 'expired' UI.
    supabase.auth
      .verifyOtp({ token_hash: tokenHash, type: otpType as EmailOtpType })
      .then(({ error }) => {
        // Genuine failure (reused/expired link): no session will arrive →
        // clear the flag so the 'expired' UI shows. On success, leave the flag
        // set; onAuthStateChange → setSession → status useMemo handles it.
        if (error) setVerifyingOtp(false)
      })
      .catch(() => {
        // Rejected promise (network / unexpected) — same as a genuine failure.
        setVerifyingOtp(false)
      })
  }, [])

  // Status dérivé de l'état AuthProvider + URL params — pas de useState à maintenir,
  // donc pas de `setState in useEffect` (anti-pattern React 19 eslint-plugin-react-hooks).
  // detectSessionInUrl (lib/supabase.ts) déclenche l'exchange PKCE au mount ;
  // onAuthStateChange dans AuthProvider flip `session` quand l'exchange réussit.
  const status = useMemo<Status>(() => {
    // Guard order matters: `session` MUST be checked before `verifyingOtp`.
    // A successful verifyOtp lands the session via onAuthStateChange but leaves
    // verifyingOtp set (it is only cleared on failure). If verifyingOtp were
    // checked first, status would stay stuck on 'loading' forever and the
    // redirect useEffect would never fire — the user lands on /auth/callback
    // and never reaches /dashboard.
    if (session) return 'redirecting'
    if (verifyingOtp) return 'loading'   // verifyOtp in flight, no session yet → "Connexion en cours…", not the 'expired' flicker
    if (loading) return 'loading'
    // Plus en loading + pas de session ⇒ échec. Distingue expired / invalid via URL.
    const params = new URLSearchParams(window.location.search)
    const errorCode = params.get('error_code') || params.get('error')
    if (errorCode === 'otp_expired' || errorCode === 'access_denied') return 'expired'
    if (errorCode) return 'invalid'
    // Pas de session, pas d'erreur URL — cas rare. Traiter comme expired (UX plus gentille).
    return 'expired'
  }, [session, loading, verifyingOtp])

  // Side effect pur : redirect quand session apparaît.
  // Lit `?next=` depuis la query string et valide via resolveNext() (D-09).
  //
  // Phase 49 F1 (D-01) — La branche ?admin=true qui appelait
  // supabase.rpc('grant_admin_role') a été supprimée (Gemini Gate 1 in-execution
  // verdict drop-and-redirect, cf. 49-03-GEMINI-GATE-1.md §Verdict). Le RPC
  // 0-arg est DROPped et le 1-arg variant n'est appelable que via service_role
  // depuis le nouvel Edge Function accept-admin-invite. La nouvelle route
  // /admin/accept-invite (AdminAcceptInvite.tsx) consomme directement le
  // magic-link token-hash sans passer par /auth/callback — le redirectTo des
  // emails d'invitation admin pointe désormais vers /admin/accept-invite (mis
  // à jour dans invite-admin/index.ts dans le même PR).
  useEffect(() => {
    if (status !== 'redirecting') return
    const params = new URLSearchParams(window.location.search)
    const redirectTo = params.get('token_hash') ? readRedirectTo(window.location.search) : null
    const target = resolveNext((redirectTo?.searchParams ?? params).get('next'))
    // The email link always lands on the unprefixed (French) document; the
    // language lives in redirect_to's path. Language is fixed per document
    // (router basename, D-05), so a language change needs a document navigation.
    const lang = redirectTo ? (langFromPath(redirectTo.pathname) ?? 'fr') : null
    if (lang && lang !== (langFromPath(window.location.pathname) ?? 'fr')) {
      window.location.replace(withLang(target, lang))
      return
    }
    navigate(target, { replace: true })
  }, [status, navigate])

  return (
    <>
      <Header offHome />
      <main
        className="pt-28 pb-20 px-6 min-h-[calc(100vh-64px-80px)]"
        aria-live="polite"
      >
        <div className="max-w-[420px] mx-auto flex items-center justify-center min-h-[200px]">
          {(status === 'loading' || status === 'redirecting') && (
            <p className="text-text-muted text-sm">{t('authCallback.loading')}</p>
          )}

          {status === 'expired' && (
            <div className="bg-surface border border-border rounded-[8px] p-6 md:p-8 w-full">
              <span className="text-xs font-medium uppercase tracking-[0.15em] text-text-muted mb-4 block">
                {t('login.eyebrow')}
              </span>
              <h1 className="font-display font-[800] text-[clamp(1.4rem,3vw,2rem)] leading-tight mb-4">
                {t('authCallback.expired.title')}
              </h1>
              <p className="text-text-muted text-base mb-6">
                {t('authCallback.expired.body')}
              </p>
              <Link
                to="/login"
                className="inline-block bg-accent hover:bg-accent-hover text-[#1a1209] font-medium px-6 py-3 rounded-[4px] transition-all duration-200 hover:-translate-y-px cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-bg w-full text-center"
              >
                {t('authCallback.backToLogin')}
              </Link>
            </div>
          )}

          {status === 'invalid' && (
            <div className="bg-surface border border-border rounded-[8px] p-6 md:p-8 w-full">
              <span className="text-xs font-medium uppercase tracking-[0.15em] text-text-muted mb-4 block">
                {t('login.eyebrow')}
              </span>
              <h1 className="font-display font-[800] text-[clamp(1.4rem,3vw,2rem)] leading-tight mb-4">
                {t('authCallback.invalid.title')}
              </h1>
              <p className="text-text-muted text-base mb-6">
                {t('authCallback.invalid.body')}
              </p>
              <Link
                to="/login"
                className="inline-block bg-accent hover:bg-accent-hover text-[#1a1209] font-medium px-6 py-3 rounded-[4px] transition-all duration-200 hover:-translate-y-px cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-bg w-full text-center"
              >
                {t('authCallback.backToLogin')}
              </Link>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  )
}
